import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { CreditCard, ShieldCheck, Sparkles, Building2, Copy, FileText, Download } from 'lucide-react';

const statusPill = (s) => {
  if (s === 'paid') return 'status-approved';
  if (s === 'rejected') return 'status-rejected';
  if (s === 'manual_submitted' || s === 'awaiting_manual_confirmation' || s === 'pending' || s === 'created') return 'status-pending';
  return 'status-needs';
};

const emptyManual = {
  open: false, tx: null,
  reference: '', notes: '',
  amount_paid: '', payer_name: '',
  paid_at: '', screenshot_url: '',
};

const Billing = () => {
  const [sub, setSub] = useState(null);
  const [expiredSub, setExpiredSub] = useState(null);
  const [txs, setTxs] = useState([]);
  const [config, setConfig] = useState(null);
  const [gstConfig, setGstConfig] = useState(null);
  const [modal, setModal] = useState(emptyManual);
  const [shotUploading, setShotUploading] = useState(false);

  const load = async () => {
    const [s, t, c, g] = await Promise.all([
      api.get('/payments/subscription/status'),
      api.get('/payments/mine'),
      api.get('/payments/config'),
      api.get('/gst/settings/public').catch(() => ({ data: null })),
    ]);
    setSub(s.data?.active || null); setExpiredSub(s.data?.expired || null);
    setTxs(t.data); setConfig(c.data); setGstConfig(g.data);
  };

  useEffect(() => { load(); }, []);

  const submitManual = async () => {
    const utr = (modal.reference || '').trim();
    if (!utr) return toast.error('Enter UTR / transaction reference');
    try {
      await api.post('/payments/manual/submit', {
        order_id: modal.tx.id,
        reference: utr,
        utr,
        notes: modal.notes,
        amount_paid: modal.amount_paid ? Number(modal.amount_paid) : null,
        payer_name: modal.payer_name || null,
        paid_at: modal.paid_at || null,
        screenshot_url: modal.screenshot_url || null,
      });
      toast.success('Reference submitted. Admin will review shortly.');
      setModal(emptyManual);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Failed';
      toast.error(detail);
    }
  };

  const handleScreenshotFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please choose an image file'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Image too large (max 5MB)'); return; }
    setShotUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/payments/manual/upload-screenshot', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setModal(s => ({ ...s, screenshot_url: data.public_url }));
      toast.success('Screenshot uploaded');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setShotUploading(false);
      e.target.value = '';
    }
  };

  const downloadInvoice = async (invoiceId) => {
    try {
      const res = await api.get(`/invoices/${invoiceId}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = url; link.download = 'invoice.pdf';
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Could not download invoice'); }
  };

  return (
    <div className='space-y-6'>
      <div>
        <h1 className='text-2xl font-semibold tracking-tight'>Billing & Plans</h1>
        <p className='text-muted-foreground text-sm'>Manage your subscription, boosts, and payment history. See <Link to='/dashboard/invoices' className='text-primary underline'>all invoices</Link>.</p>
      </div>

      <Card className='p-6 flex flex-col sm:flex-row items-start justify-between gap-4' data-testid='billing-current-plan'>
        <div className='flex items-center gap-3'>
          <div className='h-11 w-11 rounded-lg bg-secondary flex items-center justify-center'><Sparkles className='h-5 w-5 text-primary' /></div>
          <div>
            <div className='text-xs uppercase tracking-wider text-muted-foreground'>Current plan</div>
            <div className='text-lg font-semibold mt-0.5'>{sub?.plan_name || 'Free'}</div>
            {sub && <div className='text-xs text-muted-foreground'>Active until {new Date(sub.expires_at).toLocaleDateString()}</div>}
            {sub && <div className='text-xs text-muted-foreground'>Listing quota: {sub.listing_quota ?? 'Unlimited'} · Featured included: {sub.featured_quota ?? 0}</div>}
            {!sub && expiredSub && (
              <div className='text-xs text-amber-700 mt-0.5' data-testid='billing-expired-note'>
                Your {expiredSub.plan_name || 'previous'} plan expired on {new Date(expiredSub.expires_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })} — renew to restore paid benefits.
              </div>
            )}
          </div>
        </div>
        <Link to='/pricing'><Button data-testid='billing-view-plans'>{!sub && expiredSub ? 'Renew plan' : 'View plans'}</Button></Link>
      </Card>

      <Card className='p-6'>
        <div className='font-semibold mb-3 flex items-center gap-2'><CreditCard className='h-4 w-4' /> Payment history</div>
        {txs.length === 0 ? (
          <div className='text-sm text-muted-foreground'>No transactions yet.</div>
        ) : (
          <div className='overflow-x-auto'>
            <table className='w-full text-sm' data-testid='billing-transactions'>
              <thead className='text-left'>
                <tr className='border-b'>
                  <th className='py-2 pr-3'>Date</th>
                  <th className='py-2 pr-3'>Plan</th>
                  <th className='py-2 pr-3'>Amount</th>
                  <th className='py-2 pr-3'>Method</th>
                  <th className='py-2 pr-3'>Status</th>
                  <th className='py-2 pr-3'>Invoice</th>
                  <th className='py-2 pr-3'>Actions</th>
                </tr>
              </thead>
              <tbody>
                {txs.map(t => (
                  <tr key={t.id} className='border-b last:border-b-0'>
                    <td className='py-2 pr-3 text-muted-foreground'>{new Date(t.created_at).toLocaleDateString()}</td>
                    <td className='py-2 pr-3 font-medium'>{t.plan_name}</td>
                    <td className='py-2 pr-3 tabular-nums'>₹ {Number(t.grand_total ?? t.amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td className='py-2 pr-3'>{t.method}</td>
                    <td className='py-2 pr-3'>
                      <span className={`inline-flex items-center rounded-full border text-[11px] px-2 py-0.5 font-medium ${statusPill(t.status)}`}>{t.status}</span>
                    </td>
                    <td className='py-2 pr-3'>
                      {t.invoice_number ? (
                        <button
                          type='button'
                          className='inline-flex items-center gap-1 text-primary hover:underline text-xs font-mono'
                          onClick={() => downloadInvoice(t.invoice_id)}
                          data-testid={`billing-invoice-download-${t.id}`}
                        >
                          <FileText className='h-3.5 w-3.5' /> {t.invoice_number}
                        </button>
                      ) : <span className='text-xs text-muted-foreground'>—</span>}
                    </td>
                    <td className='py-2 pr-3'>
                      {t.method === 'manual' && (t.status === 'awaiting_manual_confirmation' || t.status === 'created' || t.status === 'manual_submitted') && (
                        <Button size='sm' variant='outline' onClick={() => setModal({ ...emptyManual, open: true, tx: t, amount_paid: t.grand_total ?? t.amount })} data-testid={`billing-submit-ref-${t.id}`}>
                          {t.status === 'manual_submitted' ? 'Update reference' : 'Submit payment ref'}
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {config && (
        <Card className='p-6'>
          <div className='font-semibold mb-3 flex items-center gap-2'><Building2 className='h-4 w-4' /> Manual payment (bank / UPI)</div>
          <p className='text-sm text-muted-foreground mb-3'>If Razorpay isn’t available, you can pay via bank transfer or UPI and submit the reference. Our team activates the plan within 24 hours.</p>
          <div className='grid sm:grid-cols-3 gap-3 text-sm'>
            {config.bank_transfer_available && (
              <div className='rounded-lg border p-3 bg-secondary/40' data-testid='billing-bank-card'>
                <div className='text-xs text-muted-foreground'>Bank account</div>
                <div className='font-medium'>{config.bank_details?.account_name}</div>
                {config.bank_details?.bank_name && <div className='text-xs'>{config.bank_details.bank_name}{config.bank_details?.branch ? ` · ${config.bank_details.branch}` : ''}</div>}
                <div className='mono'>{config.bank_details?.account_number}</div>
                <div className='text-xs'>IFSC: {config.bank_details?.ifsc}</div>
              </div>
            )}
            {(gstConfig?.upi_id || config.bank_details?.upi) && (
            <div className='rounded-lg border p-3 bg-secondary/40'>
              <div className='text-xs text-muted-foreground'>UPI ID</div>
              <div className='mono flex items-center gap-2' data-testid='billing-upi-id'>
                {gstConfig?.upi_id || config.bank_details?.upi}
                <button
                  type='button'
                  onClick={() => { navigator.clipboard.writeText(gstConfig?.upi_id || config.bank_details?.upi || ''); toast.success('Copied'); }}
                >
                  <Copy className='h-3 w-3 text-muted-foreground' />
                </button>
              </div>
            </div>
            )}
            {gstConfig?.upi_qr_url && (
              <div className='rounded-lg border p-3 bg-secondary/40 flex items-center gap-3'>
                <img src={gstConfig.upi_qr_url} alt='UPI QR' className='h-20 w-20 object-contain rounded' data-testid='billing-upi-qr' />
                <div className='text-xs text-muted-foreground'>Scan with any UPI app</div>
              </div>
            )}
          </div>
        </Card>
      )}

      <Dialog open={modal.open} onOpenChange={(o) => setModal(s => ({ ...s, open: o }))}>
        <DialogContent className='max-w-lg'>
          <DialogHeader>
            <DialogTitle>Submit manual payment</DialogTitle>
            <DialogDescription>Enter the UPI/UTR reference and payment details so an admin can verify and activate your plan.</DialogDescription>
          </DialogHeader>
          <div className='space-y-3'>
            <div className='text-sm text-muted-foreground'>
              Order: <strong>{modal.tx?.plan_name}</strong>
              {modal.tx?.taxable_amount != null && modal.tx?.grand_total != null ? (
                <div className='mt-1 rounded border bg-secondary/40 p-2 text-xs'>
                  <div>Taxable: ₹ {Number(modal.tx.taxable_amount).toFixed(2)}</div>
                  <div>GST ({modal.tx.gst_rate_pct}%): ₹ {Number(modal.tx.total_tax).toFixed(2)}</div>
                  <div className='font-semibold border-t mt-1 pt-1'>Payable: ₹ {Number(modal.tx.grand_total).toFixed(2)}</div>
                </div>
              ) : (
                <span> — ₹ {modal.tx?.amount}</span>
              )}
            </div>
            {gstConfig?.upi_id && (
              <div className='rounded-lg border p-3 bg-secondary/40 flex gap-3 items-center'>
                {gstConfig.upi_qr_url && <img src={gstConfig.upi_qr_url} alt='UPI QR' className='h-24 w-24 object-contain rounded' />}
                <div className='text-xs'>
                  <div className='text-muted-foreground'>Pay to UPI ID</div>
                  <div className='mono font-medium text-sm'>{gstConfig.upi_id}</div>
                </div>
              </div>
            )}
            <div><Label>UPI / UTR / Payment reference *</Label><Input value={modal.reference} onChange={(e) => setModal(s => ({ ...s, reference: e.target.value }))} placeholder='e.g. HDFC/UTR/123456789' data-testid='manual-ref-input' /></div>
            <div className='grid grid-cols-2 gap-3'>
              <div>
                <Label>Amount paid (₹)</Label>
                <Input type='number' value={modal.amount_paid} onChange={(e) => setModal(s => ({ ...s, amount_paid: e.target.value }))} data-testid='manual-amount-input' />
              </div>
              <div>
                <Label>Payment date/time</Label>
                <Input type='datetime-local' value={modal.paid_at} onChange={(e) => setModal(s => ({ ...s, paid_at: e.target.value }))} data-testid='manual-paidat-input' />
              </div>
            </div>
            <div>
              <Label>Payer name</Label>
              <Input value={modal.payer_name} onChange={(e) => setModal(s => ({ ...s, payer_name: e.target.value }))} data-testid='manual-payer-input' />
            </div>
            <div>
              <Label>Upload payment screenshot (optional)</Label>
              <Input type='file' accept='image/*' onChange={handleScreenshotFile} disabled={shotUploading} data-testid='manual-screenshot-file' />
              {shotUploading && <div className='text-xs text-muted-foreground mt-1'>Uploading…</div>}
              {modal.screenshot_url && !shotUploading && (
                <div className='mt-2 flex items-center gap-3'>
                  <img src={modal.screenshot_url} alt='Payment screenshot' className='h-16 w-16 object-cover rounded border' data-testid='manual-screenshot-preview' />
                  <button
                    type='button'
                    className='text-xs text-red-600 hover:underline'
                    onClick={() => setModal(s => ({ ...s, screenshot_url: '' }))}
                    data-testid='manual-screenshot-remove'
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>
            <div><Label>Notes (optional)</Label><Textarea rows={2} value={modal.notes} onChange={(e) => setModal(s => ({ ...s, notes: e.target.value }))} /></div>
            <div className='flex items-start gap-2 text-xs text-muted-foreground rounded bg-amber-50 border border-amber-200 p-2'>
              <ShieldCheck className='h-4 w-4 text-amber-600 shrink-0 mt-0.5' />
              Your plan is activated ONLY after our team verifies this UPI payment. Duplicate references are auto-flagged.
            </div>
          </div>
          <DialogFooter>
            <Button variant='outline' onClick={() => setModal(emptyManual)}>Cancel</Button>
            <Button onClick={submitManual} data-testid='manual-ref-submit'>Submit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Billing;
